import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { Modal as RNModal, Platform } from "react-native";
import Animated, {
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";

import { useKeyboardBottomInset } from "../../../hooks/useKeyboardBottomInset";
import { usePreventScroll } from "../../../hooks/usePreventScroll";
import {
  AnimationDurations,
  AnimationEasings,
} from "../../../theme/animations";
import { Box } from "../../primitive";
import { Overlay } from "../Overlay/Overlay";
import { WhooshToastProvider } from "../Toast/WhooshToastProvider";
import { ModalContentProps, ModalProps } from "./types";
import { useCoordinatedNativeModal } from "./useCoordinatedNativeModal";
import { useIsModalFocused } from "./useIsModalFocused";

const ModalBase = ({
  isOpen,
  onClose,
  children,
  presentation = "default",
  transparentBackground,
}: ModalProps) => {
  const [isClosing, setIsClosing] = useState(false);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isFocused = useIsModalFocused();

  const handleInternalClose = useCallback(() => {
    setInternalIsOpen(false);
    setIsClosing(false);
    onClose?.();
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (onClose) {
      setIsClosing(true);
      handleInternalClose();
    }
  }, [handleInternalClose, onClose]);

  useEffect(() => {
    if (isOpen) {
      /**
       * Native modal sequencing (second modal stuck / clash when using transparent modals)
       * is handled by `useCoordinatedNativeModal` + `ModalTransitionProvider`.
       *
       * @see https://github.com/facebook/react-native/issues/29929
       */
      setIsClosing(false);
      setInternalIsOpen(true);
    } else if (!isOpen && internalIsOpen) {
      setIsClosing(true);
      handleInternalClose?.();
    }
  }, [handleInternalClose, internalIsOpen, isOpen]);

  usePreventScroll({ isOpen });

  const isModalOpen = isOpen || internalIsOpen;

  const { rnModalVisible, onDismiss } = useCoordinatedNativeModal(
    isFocused && isModalOpen
  );

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return;
    }

    const handleEscapePress = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isModalOpen) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscapePress);

    return () => {
      document.removeEventListener("keydown", handleEscapePress);
    };
  }, [handleClose, isModalOpen]);

  return isFocused ? (
    <RNModal
      visible={rnModalVisible}
      transparent={true}
      onRequestClose={handleClose}
      onDismiss={onDismiss}
    >
      <WhooshToastProvider>
        {presentation === "fullscreen" ? (
          <Box flex={1} position="relative">
            {children}
          </Box>
        ) : (
          <Content isClosing={isClosing}>
            <Overlay
              onPress={handleClose}
              isClosing={isClosing}
              transparentBackground={transparentBackground}
            />
            {children}
          </Content>
        )}
      </WhooshToastProvider>
    </RNModal>
  ) : null;
};

const ModalContent = ({ children, ...props }: ModalContentProps) => (
  <Box
    backgroundColor="surface-primary"
    borderRadius={{ base: "0", md: "6" }}
    overflow="hidden"
    position="relative"
    zIndex={10}
    {...props}
  >
    {children}
  </Box>
);

export const Modal = Object.assign(ModalBase, { Content: ModalContent });

const Content = ({
  isClosing,
  children,
}: {
  isClosing: boolean;
  children: ReactNode;
}) => {
  const bottomInset = useKeyboardBottomInset();
  const [internalIsClosing, setInternalIsClosing] = useState(false);

  useEffect(() => {
    setInternalIsClosing(isClosing);
  }, [isClosing]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: withTiming(internalIsClosing ? 0 : 1, {
        duration: AnimationDurations.moderate02,
        easing: internalIsClosing
          ? AnimationEasings.exit
          : AnimationEasings.entrance,
      }),
      top: withTiming(internalIsClosing ? 0 : 0, {
        duration: AnimationDurations.moderate02,
        easing: internalIsClosing
          ? AnimationEasings.exit
          : AnimationEasings.entrance,
      }),
    };
  }, [internalIsClosing]);

  return (
    <Animated.View
      style={[
        {
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignContent: "center",
          alignItems: "center",
          paddingBottom: Platform.OS === "android" ? bottomInset : 0,
        },
        animatedStyle,
      ]}
    >
      {children}
    </Animated.View>
  );
};
